/**
 * Icon "ⓘ" + popover giải thích chi tiết từng số liệu trên Dashboard.
 * Nội dung giải thích phụ thuộc kiểu thời gian đang chọn (ngày đặt đơn vs ngày
 * sàn duyệt) vì cùng một ô số nhưng cách gom dữ liệu khác nhau.
 */
import { Popover } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo } from "@fortawesome/free-solid-svg-icons";

/** Nhãn ngắn của 2 kiểu thời gian — dùng lại ở toggle + phần giải thích. */
export const TIME_FIELD_LABELS = {
    purchase: "Thời gian mua sắm",
    complete: "Thời gian hoàn thành",
};

const SOURCE_NOTE = {
    purchase:
        "Nguồn: bảng Orders, tính trực tiếp theo purchaseTime (thời điểm user bấm mua trên sàn).",
    complete:
        "Nguồn: snapshot DailyStats theo completeTime; riêng hôm nay tính live từ Orders để số không bị trễ.",
};

/**
 * Tạo bộ giải thích cho các ô số liệu.
 * @param {'purchase'|'complete'} timeField
 */
export function buildMetricDocs(timeField) {
    const byPurchase = timeField === "purchase";
    const grouping = byPurchase
        ? "xếp theo NGÀY ĐẶT ĐƠN (purchaseTime)"
        : "xếp theo NGÀY SÀN DUYỆT ĐƠN (completeTime)";

    return {
        timeField: {
            title: "Kiểu thời gian",
            body: [
                `${TIME_FIELD_LABELS.purchase}: đơn được xếp vào ngày user bấm mua. Trả lời câu hỏi "ngày đó bán được bao nhiêu".`,
                `${TIME_FIELD_LABELS.complete}: đơn được xếp vào ngày sàn duyệt (chốt tiền). Trả lời câu hỏi "ngày đó nhận về bao nhiêu tiền".`,
                "Cùng một khoảng ngày, 2 kiểu cho ra 2 con số khác nhau là bình thường: đơn mua tháng này thường tới 1–3 tháng sau mới được duyệt.",
                "Kiểu thời gian áp dụng cho: Tổng hoa hồng, Hoa hồng user, biểu đồ doanh thu và tỷ lệ theo sàn. KHÔNG áp dụng cho 2 ô tiền chuyển khoản.",
            ],
        },
        totalCommission: {
            title: "Tổng hoa hồng",
            body: [
                `Tổng hoa hồng sàn trả về của các đơn ĐÃ DUYỆT (conversionStatus = 2), ${grouping}.`,
                byPurchase
                    ? "Đơn mới đặt còn đang chờ sàn duyệt CHƯA được tính vào ô này (xem dòng “đơn chờ duyệt” ngay dưới thẻ) — nên số của kỳ gần đây sẽ còn tăng dần."
                    : "Đơn hủy và đơn chờ duyệt không được tính. Số của một ngày trong quá khứ gần như không đổi nữa.",
                SOURCE_NOTE[timeField],
            ],
        },
        totalUserCommission: {
            title: "Hoa hồng user",
            body: [
                "Phần hoa hồng hoàn lại cho user (userCommission) của đúng những đơn được tính ở ô Tổng hoa hồng.",
                "Tổng hoa hồng − Hoa hồng user = phần hệ thống giữ lại (chưa trừ chi phí khác).",
                "Chưa bao gồm hoa hồng giới thiệu (refCommission) và thưởng thủ công.",
                SOURCE_NOTE[timeField],
            ],
        },
        totalPaid: {
            title: "Đã chuyển khoản",
            body: [
                "Tổng số tiền thực tế đã chuyển cho user (lệnh rút trạng thái Đã thanh toán).",
                "Luôn tính theo THỜI ĐIỂM CHUYỂN KHOẢN, không đổi theo kiểu thời gian đơn hàng đang chọn.",
                "Không so được 1-1 với Hoa hồng user trong cùng khoảng: user tích lũy rồi rút ở thời điểm khác.",
            ],
        },
        pendingPayments: {
            title: "Chờ xử lý",
            body: [
                "Số yêu cầu rút tiền đang chờ duyệt tại thời điểm hiện tại.",
                "Là TỔNG TOÀN HỆ THỐNG — không lọc theo khoảng ngày và không đổi theo kiểu thời gian.",
            ],
        },
        revenueChart: {
            title: "Doanh thu theo ngày",
            body: [
                `Hoa hồng sàn và hoa hồng user của từng ngày trong 30 ngày gần nhất, ${grouping}.`,
                "Biểu đồ này LUÔN là 30 ngày gần nhất — không chịu ảnh hưởng của bộ lọc ngày ở đầu trang, nhưng có đổi theo kiểu thời gian.",
                byPurchase
                    ? "Ngày gần hôm nay thường thấp vì phần lớn đơn chưa được sàn duyệt."
                    : "Cột hôm nay là số live, có thể tăng thêm trong ngày.",
            ],
        },
        platformChart: {
            title: "Tỷ lệ theo sàn",
            body: [
                "Tỷ trọng hoa hồng sàn (Shopee / Lazada / TikTok) của đúng tập đơn ở ô Tổng hoa hồng.",
                "Dùng chung bộ lọc ngày và kiểu thời gian với các thẻ tổng phía trên.",
            ],
        },
    };
}

/** Icon ⓘ nhỏ, bấm vào hiện popover giải thích. */
export function MetricInfo({ doc, placement = "bottom" }) {
    if (!doc) return null;
    return (
        <Popover
            trigger="click"
            placement={placement}
            title={<span className="font-semibold">{doc.title}</span>}
            content={
                <ul className="m-0 pl-4 max-w-xs text-xs text-content-2 leading-relaxed space-y-1.5">
                    {doc.body.map((line, i) => (
                        <li key={i}>{line}</li>
                    ))}
                </ul>
            }
        >
            <button
                type="button"
                aria-label={`Giải thích: ${doc.title}`}
                onClick={(e) => e.stopPropagation()}
                className="p-0.5 text-content-3 hover:text-blue-500 transition-colors leading-none"
            >
                <FontAwesomeIcon icon={faCircleInfo} className="w-3.5 h-3.5" />
            </button>
        </Popover>
    );
}

export default MetricInfo;
